import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { DragIndicatorRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useGetPlanStructure, useReorderPlanStructure } from "../../../../http";
import { GetPlanStructureDto } from "../../../../http/types";
import { lexorank } from "../../../../utilities";

const BlockContainer = styled(Stack)<{ $isDragging: boolean }>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.line.main};
  background-color: ${({ theme }) => theme.palette.common.white};
  border-radius: 8px;
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  .dragHandle {
    opacity: 1;
    @media (pointer: fine) {
      opacity: ${({ $isDragging }) => ($isDragging ? 1 : 0)};
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.opacity5};

    .dragHandle {
      opacity: 1;
    }
  }
`;

const StyledStack = styled(Stack)<{ $isDragging: boolean }>`
  padding-left: ${({ theme }) => theme.spacing(0)};
  width: 100%;
  transition: ${({ theme }) => theme.transitions.create("background-color")};
  background-color: ${({ $isDragging, theme }) => ($isDragging ? theme.palette.primary.opacity5 : undefined)};

  .dragHandle {
    opacity: 1;
    @media (pointer: fine) {
      opacity: ${({ $isDragging }) => ($isDragging ? 1 : 0)};
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.transparent5};

    .dragHandle {
      opacity: 1;
    }
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const DragHandle = styled(Box)`
  color: ${({ theme }) => theme.palette.grey[200]};
  background-color: transparent;
  @media (pointer: fine) {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.primary.opacity10};
  }
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.create(["opacity"])};
  cursor: pointer;
  min-width: 20px;
`;

const DroppableZone = styled(Stack)<{ $isDraggingOver: boolean }>`
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  background-color: ${({ $isDraggingOver, theme }) =>
    $isDraggingOver ? theme.palette.primary.opacity5 : theme.palette.common.white};
  pointer-events: ${({ $isDraggingOver }) => ($isDraggingOver ? "none" : "auto")};

  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

interface IProps {
  visible: boolean;
  planGuid: string;
  onClose: () => void;
}

const enum DragItemType {
  BLOCK = "BLOCK",
  SECTION = "SECTION",
}

const EditPlanStructureModal = (props: IProps) => {
  const { data: planStructure, isLoading } = useGetPlanStructure(props.planGuid);
  const { mutate: reorderPlanStructure, isLoading: isSaving } = useReorderPlanStructure();

  const [localState, setLocalState] = useState<GetPlanStructureDto>({ blocks: [] });

  useEffect(() => {
    if (planStructure?.blocks) {
      setLocalState({
        ...planStructure,
        blocks: [...planStructure.blocks].sort((a, b) => a.sortOrder.localeCompare(b.sortOrder)),
      });
    }
  }, [props.visible, planStructure]);

  function handleDragEnd(result: DropResult) {
    const { draggableId, destination, source, type } = result;

    // Dragged outside container
    if (!destination) {
      return;
    }

    // Dragged back to same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    if (type === DragItemType.BLOCK) {
      handleBlockReordered(source.index, destination.index);
    } else if (type === DragItemType.SECTION) {
      handleSectionReordered(draggableId, source.droppableId, destination.droppableId, source.index, destination.index);
    }
  }

  function handleBlockReordered(sourceIndex: number, destinationIndex: number) {
    const editCopy = { ...localState };

    const [movedBlock] = editCopy.blocks.splice(sourceIndex, 1);
    editCopy.blocks.splice(destinationIndex, 0, movedBlock);

    const newSortOrder = lexorank.getRank(editCopy.blocks, sourceIndex, destinationIndex);

    const updatedBlocks = editCopy.blocks.map((block) => {
      if (block.guid === movedBlock.guid) {
        return { ...block, sortOrder: newSortOrder };
      }
      return block;
    });

    setLocalState({ ...localState, blocks: updatedBlocks });
  }

  function handleSectionReordered(
    sectionGuid: string,
    sourceBlockGuid: string,
    destinationBlockGuid: string,
    sourceIndex: number,
    destinationIndex: number,
  ) {
    const newBlocks = localState.blocks.map((block) => ({
      ...block,
      sections: [...block.sections].sort((a, b) => a.sortOrder.localeCompare(b.sortOrder)),
    }));

    const sourceBlock = newBlocks.find((b) => b.guid === sourceBlockGuid);
    const destinationBlock = newBlocks.find((b) => b.guid === destinationBlockGuid);

    if (!sourceBlock || !destinationBlock) {
      return;
    }

    const section = sourceBlock.sections.find((s) => s.guid === sectionGuid);
    if (!section) {
      return;
    }

    let newSortOrder: string;
    if (sourceBlockGuid === destinationBlockGuid) {
      newSortOrder = lexorank.getRank(sourceBlock.sections, sourceIndex, destinationIndex);
      section.sortOrder = newSortOrder;
    } else {
      sourceBlock.sections = sourceBlock.sections.filter((s) => s.guid !== sectionGuid);
      newSortOrder = lexorank.getRankAtIndex(destinationBlock.sections, destinationIndex);
      destinationBlock.sections.splice(destinationIndex, 0, { ...section, sortOrder: newSortOrder });
    }

    setLocalState({ ...localState, blocks: newBlocks });
  }

  function handleSaveClicked() {
    reorderPlanStructure(
      {
        planGuid: props.planGuid,
        blocks: localState.blocks.map((block) => ({
          guid: block.guid,
          sortOrder: block.sortOrder,
          sections: block.sections.map((section) => ({
            guid: section.guid,
            sortOrder: section.sortOrder,
          })),
        })),
      },
      {
        onSuccess: () => {
          props.onClose();
        },
      },
    );
  }

  function handleClose(_event: object, reason?: "backdropClick" | "escapeKeyDown") {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    props.onClose();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Edit Plan Structure</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Typography>Organize the strategic map by reordering blocks and their sections.</Typography>
          {isLoading ? (
            <Box sx={{ alignSelf: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="plan" direction="horizontal" type={DragItemType.BLOCK}>
                {(provided) => (
                  <Stack direction="row" spacing={1} {...provided.droppableProps} ref={provided.innerRef}>
                    {localState.blocks.map((block, blockIndex) => (
                      <Draggable key={block.guid} draggableId={block.guid} index={blockIndex}>
                        {(blockProvided, blockSnapshot) => (
                          <Box
                            ref={blockProvided.innerRef}
                            {...blockProvided.draggableProps}
                            sx={{ width: "100%", overflow: "hidden" }}
                          >
                            <BlockContainer $isDragging={blockSnapshot.isDragging}>
                              <Stack direction="row" sx={{
                                alignItems: "center"
                              }}>
                                <DragHandle className="dragHandle" {...blockProvided.dragHandleProps}>
                                  <DragIndicatorRounded />
                                </DragHandle>
                                <Typography variant="body2" sx={{ p: 0.5 }} noWrap>
                                  {block.name}
                                </Typography>
                              </Stack>
                              <Divider />

                              <Droppable droppableId={block.guid} type={DragItemType.SECTION}>
                                {(sectionProvided, sectionSnapshot) => (
                                  <DroppableZone
                                    $isDraggingOver={sectionSnapshot.isDraggingOver}
                                    {...sectionProvided.droppableProps}
                                    ref={sectionProvided.innerRef}
                                  >
                                    {block.sections.length === 0 ? (
                                      <Typography variant="placeholder" sx={{ p: 0.5 }}>
                                        Nothing here yet.
                                      </Typography>
                                    ) : (
                                      [...block.sections]
                                        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
                                        .map((section, sectionIndex) => (
                                          <Draggable key={section.guid} draggableId={section.guid} index={sectionIndex}>
                                            {(sectionItemProvided, sectionItemSnapshot) => (
                                              <StyledStack
                                                ref={sectionItemProvided.innerRef}
                                                {...sectionItemProvided.draggableProps}
                                                $isDragging={sectionItemSnapshot.isDragging}
                                                direction="row"
                                              >
                                                <DragHandle
                                                  className="dragHandle"
                                                  {...sectionItemProvided.dragHandleProps}
                                                >
                                                  <DragIndicatorRounded />
                                                </DragHandle>
                                                <Typography sx={{ p: 0.5 }} noWrap>
                                                  {section.name}
                                                </Typography>
                                              </StyledStack>
                                            )}
                                          </Draggable>
                                        ))
                                    )}
                                    {sectionProvided.placeholder}
                                  </DroppableZone>
                                )}
                              </Droppable>
                            </BlockContainer>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveClicked}
          disabled={isSaving}
          endIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { EditPlanStructureModal };
